import { useState } from 'react';
import { Table, Tag, Input, Select, Space } from 'antd';
import type { Batch } from '@/types/nacha';
import { formatAmount, formatDate, getServiceClassLabel } from '@/utils/export';

interface BatchesViewProps {
  batches: Batch[];
}

function BatchesView({ batches }: BatchesViewProps) {
  const [searchText, setSearchText] = useState('');
  const [secCodeFilter, setSecCodeFilter] = useState<string>('all');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data
  const filteredBatches = batches.filter((batch) => {
    if (!searchText) {
      const matchesSecCode = 
        secCodeFilter === 'all' || batch.header.standardEntryClassCode === secCodeFilter;
      return matchesSecCode;
    }
    
    const lowerSearchText = searchText.toLowerCase();
    
    // Search across all columns
    const matchesSearch = 
      batch.header.batchNumber.includes(searchText) ||
      batch.header.companyName.toLowerCase().includes(lowerSearchText) ||
      batch.header.standardEntryClassCode.toLowerCase().includes(lowerSearchText) ||
      batch.header.companyEntryDescription.toLowerCase().includes(lowerSearchText) ||
      formatDate(batch.header.effectiveEntryDate).toLowerCase().includes(lowerSearchText) ||
      getServiceClassLabel(batch.header.serviceClassCode).toLowerCase().includes(lowerSearchText) ||
      batch.entries.length.toString().includes(searchText) ||
      formatAmount(batch.control.totalDebitAmount).includes(searchText) ||
      formatAmount(batch.control.totalCreditAmount).includes(searchText) ||
      batch.header.companyId.includes(searchText) ||
      batch.header.originatingDFI.includes(searchText);
    
    const matchesSecCode = 
      secCodeFilter === 'all' || batch.header.standardEntryClassCode === secCodeFilter;
    
    return matchesSearch && matchesSecCode;
  });

  // Get all SEC codes
  const secCodes = Array.from(new Set(batches.map(b => b.header.standardEntryClassCode)));

  const columns = [
    {
      title: 'Batch #',
      dataIndex: ['header', 'batchNumber'],
      key: 'batchNumber',
      width: 100,
      sorter: (a: Batch, b: Batch) => 
        parseInt(a.header.batchNumber) - parseInt(b.header.batchNumber),
    },
    {
      title: 'Company Name',
      dataIndex: ['header', 'companyName'],
      key: 'companyName',
      width: 200,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: 'Sec Code',
      dataIndex: ['header', 'standardEntryClassCode'],
      key: 'secCode',
      width: 100,
      render: (code: string) => (
        <Tag color="blue">{code}</Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: ['header', 'companyEntryDescription'],
      key: 'description',
      width: 150,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: 'Effective Date',
      dataIndex: ['header', 'effectiveEntryDate'],
      key: 'effectiveDate',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Service Class',
      dataIndex: ['header', 'serviceClassCode'],
      key: 'serviceClass',
      width: 180,
      render: (code: string) => getServiceClassLabel(code),
    },
    {
      title: 'Entry Count',
      key: 'entryCount',
      width: 100,
      render: (_: unknown, record: Batch) => record.entries.length,
      sorter: (a: Batch, b: Batch) => a.entries.length - b.entries.length,
    },
    {
      title: 'Debit Amount',
      dataIndex: ['control', 'totalDebitAmount'],
      key: 'debitAmount',
      width: 140,
      render: (amount: string) => (
        <span style={{ color: '#cf1322' }}>${formatAmount(amount)}</span>
      ),
      sorter: (a: Batch, b: Batch) => 
        parseInt(a.control.totalDebitAmount) - parseInt(b.control.totalDebitAmount),
    },
    {
      title: 'Credit Amount',
      dataIndex: ['control', 'totalCreditAmount'],
      key: 'creditAmount',
      width: 140,
      render: (amount: string) => (
        <span style={{ color: '#3f8600' }}>${formatAmount(amount)}</span>
      ),
      sorter: (a: Batch, b: Batch) => 
        parseInt(a.control.totalCreditAmount) - parseInt(b.control.totalCreditAmount),
    },
  ];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '16px' }}>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search all columns..."
          allowClear
          style={{ width: 300 }}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1); // Reset to first page when search changes
          }}
        />
        <Select
          value={secCodeFilter}
          onChange={(value) => {
            setSecCodeFilter(value);
            setCurrentPage(1); // Reset to first page when filter changes
          }}
          style={{ width: 150 }}
          virtual={false}
          dropdownStyle={{ minWidth: 150 }}
          transitionName=""
          dropdownMatchSelectWidth={false}
        >
          <Select.Option value="all">All</Select.Option>
          {secCodes.map((code) => (
            <Select.Option key={code} value={code}>
              {code}
            </Select.Option>
          ))}
        </Select>
      </Space>
      
      <Table
        key={`${secCodeFilter}-${searchText}-${currentPage}-${pageSize}`}
        columns={columns}
        dataSource={filteredBatches}
        rowKey={(record) => record.header.batchNumber}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '30', '40', '50'],
          showTotal: (total) => `Total ${total} batches`,
          onChange: (page) => setCurrentPage(page),
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1); // Reset to first page when page size changes
          },
        }}
        size="small"
      />
      
      <style>{`
        /* Disable pagination select dropdown slide animation */
        .ant-select-dropdown-placement-bottomLeft,
        .ant-select-dropdown-placement-topLeft {
          animation-duration: 0s !important;
        }
        
        .ant-slide-up-enter,
        .ant-slide-up-appear,
        .ant-slide-up-leave {
          animation-duration: 0s !important;
        }

        /* Prevent text wrapping in table cells */
        .ant-table-cell {
          white-space: nowrap !important;
        }
      `}</style>
    </div>
  );
}

export default BatchesView;
